// User model
export interface User {
    id: string;
    email: string;
    password_hash: string;
    plan_tier: PlanTier;
    subscription_expiry: string | null;
    is_admin?: number;
    full_name?: string | null;
    company_name?: string | null;
    vat_number?: string | null;
    billing_address?: string | null;
    phone?: string | null;
    proof_of_address_url?: string | null;
    copy_of_id_url?: string | null;
    // Custom plan fields (only used when plan_tier === 'custom')
    custom_price_cents?: number | null;
    custom_max_sites?: number | null;
    custom_max_storage?: number | null;
    custom_features?: string | null; // JSON array
    created_at: string;
    updated_at: string;
    unread_messages_count?: number; // Admin only
}

// Site model
export interface Site {
    id: string;
    user_id: string;
    domain_name: string;
    custom_hostname_id: string | null;
    status: SiteStatus;
    storage_used: number;
    blueprint_id?: string | null;
    created_at: string;
}

// Payment model
export interface Payment {
    id: string;
    user_id: string;
    yoco_payment_id: string | null;
    amount_cents: number;
    currency: string;
    status: PaymentStatus;
    plan_tier: PlanTier | null;
    created_at: string;
}

// Floating License definitions
export interface UserLicense {
    id: string;
    user_id: string;
    license_type: 'store';
    plan_tier: StoreTier;
    assigned_site_id: string | null;
    status: 'active' | 'expired' | 'cancelled';
    expires_at: string;
    created_at: string;
    updated_at: string;
}

// Enums
export type PlanTier = 'free' | 'starter' | 'hobby' | 'pro' | 'agency' | 'custom';
export type StoreTier = 'beginner_store' | 'pro_store';

export type SiteStatus = 'pending' | 'active' | 'suspended' | 'deleted';
export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded';
export type ABTestStatus = 'draft' | 'running' | 'paused' | 'completed';
export type ContactThreadStatus = 'open' | 'replied' | 'closed';

// Plan configuration
export interface HostingPlanConfig {
    name: string;
    price: number; // cents
    maxSites: number;
    maxStorage: number; // bytes
    features: string[];
}

export interface StorePlanConfig {
    name: string;
    price: number; // cents
    maxProducts: number;
    maxStorage: number; // bytes
    features: string[];
}

export const HOSTING_PLANS: Record<Exclude<PlanTier, 'custom' | 'free'>, HostingPlanConfig> = {
    starter: {
        name: 'Starter',
        price: 5000, // R50
        maxSites: 1,
        maxStorage: 300 * 1024 * 1024, // 300MB
        features: ['1 Website', '300MB Storage', 'Custom Domain', 'SSL Certificate']
    },
    hobby: {
        name: 'Hobby',
        price: 15000, // R150
        maxSites: 3,
        maxStorage: 600 * 1024 * 1024, // 600MB
        features: ['3 Websites', '600MB Storage', 'Custom Domains', 'SSL Certificates']
    },
    pro: {
        name: 'Pro',
        price: 50000, // R500
        maxSites: 10,
        maxStorage: 5 * 1024 * 1024 * 1024, // 5GB
        features: ['10 Websites', '5GB Storage', 'Custom Domains', 'SSL Certificates', 'Priority Support', 'A/B Testing']
    },
    agency: {
        name: 'Agency',
        price: 149900, // R1499
        maxSites: 25,
        maxStorage: 200 * 1024 * 1024 * 1024, // 200GB
        features: ['25 Websites', '200GB Storage', 'Custom Domains', 'Unlimited Subdomains', 'Priority Support', 'A/B Testing']
    }
};

export const STORE_PLANS: Record<StoreTier, StorePlanConfig> = {
    beginner_store: {
        name: 'Beginner Store',
        price: 38000, // R380
        maxProducts: 50,
        maxStorage: 500 * 1024 * 1024, // +500MB
        features: ['Online Store Instance', '50 Products Limit', 'Shopping Cart & Checkout', '+500MB Storage']
    },
    pro_store: {
        name: 'Pro Store',
        price: 55000, // R550
        maxProducts: 200,
        maxStorage: 1024 * 1024 * 1024, // +1GB
        features: ['Advanced Store Instance', '200 Products Limit', 'Shopping Cart & Checkout', '+1GB Storage', 'Priority Analytics']
    }
};

// A/B Testing types
export interface ABTest {
    id: string;
    site_id: string;
    user_id: string;
    name: string;
    status: ABTestStatus;
    variant_a_path: string;
    variant_b_path: string;
    split_ratio: number;
    started_at: string | null;
    completed_at: string | null;
    created_at: string;
}

export interface ABTestStats {
    test_id: string;
    variant: 'A' | 'B';
    views: number;
    date: string;
}

// Contact/Chat types
export interface ContactThread {
    id: string;
    user_id: string | null;
    email: string;
    name: string | null;
    subject: string | null;
    status: ContactThreadStatus;
    created_at: string;
    updated_at: string;
    admin_unread?: number; // 1 if unread by admin
}

export interface ContactMessage {
    id: string;
    thread_id: string;
    sender_type: 'user' | 'admin';
    message: string;
    created_at: string;
}

// Site Backup types
export interface SiteBackup {
    id: string;
    site_id: string;
    user_id: string;
    name: string | null;
    size_bytes: number;
    file_count: number;
    created_at: string;
}

// API Response types
export interface ApiResponse<T = unknown> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
}

// Auth types
export interface JwtPayload {
    userId: string;
    email: string;
    planTier: PlanTier;
    exp: number;
}

export interface LoginRequest {
    email: string;
    password: string;
}

export interface RegisterRequest {
    email: string;
    password: string;
}

export interface AuthResponse {
    token: string;
    user: Omit<User, 'password_hash'>;
}

// Helper to get global plan limits (base hosting limits)
export function getPlanLimits(user: Pick<User, 'plan_tier'> & Partial<Pick<User, 'custom_max_sites' | 'custom_max_storage'>>): { maxSites: number; maxStorage: number } {
    if (user.plan_tier === 'custom') {
        return {
            maxSites: user.custom_max_sites ?? 1,
            maxStorage: user.custom_max_storage ?? 100 * 1024 * 1024
        };
    }
    if (user.plan_tier === 'free') {
        return { maxSites: 0, maxStorage: 0 };
    }
    const plan = HOSTING_PLANS[user.plan_tier as keyof typeof HOSTING_PLANS];
    return { maxSites: plan?.maxSites || 1, maxStorage: plan?.maxStorage || 300 * 1024 * 1024 };
}

// ── Unified PLANS record ─────────────────────────────────────────────────────
// Merges hosting + store plans into a single iterable for pricing grids.
// Hosting plans get maxProducts = 0; store plans carry their real limits.
export interface UnifiedPlanConfig {
    name: string;
    price: number; // cents
    maxSites: number;
    maxStorage: number; // bytes
    maxProducts: number;
    features: string[];
}

export const PLANS: Record<string, UnifiedPlanConfig> = {
    ...Object.fromEntries(
        Object.entries(HOSTING_PLANS).map(([key, plan]) => [
            key,
            { ...plan, maxProducts: 0 } as UnifiedPlanConfig,
        ])
    ),
    ...Object.fromEntries(
        Object.entries(STORE_PLANS).map(([key, plan]) => [
            key,
            { name: plan.name, price: plan.price, maxSites: 1, maxStorage: plan.maxStorage, maxProducts: plan.maxProducts, features: plan.features } as UnifiedPlanConfig,
        ])
    ),
};

// Check if user has A/B testing access
export function hasABTestingAccess(planTier: PlanTier): boolean {
    return planTier === 'pro' || planTier === 'agency' || planTier === 'custom';
}
